import React from "react";
import { Euro, Scale, FileText, Users, Tag, ClipboardList, Banknote, ArrowRight } from "lucide-react";
import { Card } from "../ui/card";
import { useFm, fmtKg, fmtInt, Delta, SourceNote } from "./biShared";

const Kpi = ({ label, value, delta, sub, extra, icon: Icon, accent = "text-[#8A1C2A]", note, testId }) => (
  <Card className="bg-white border-stone-200 p-4 flex flex-col gap-1 min-h-[112px]" data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] text-stone-500 uppercase tracking-wide font-semibold">{label}</p>
      <div className="flex items-center gap-1 shrink-0">
        <Icon className="w-5 h-5 text-stone-300" />
        {note && <SourceNote text={note} testId={`${testId}-source`} />}
      </div>
    </div>
    <p className={`text-xl sm:text-3xl font-bold tabular-nums ${accent}`}>{value}</p>
    {extra && <p className="text-xs sm:text-sm text-stone-600 tabular-nums -mt-0.5" data-testid={`${testId}-extra`}>{extra}</p>}
    <div className="flex items-center justify-between gap-2 flex-wrap mt-auto">
      {delta !== undefined && <Delta value={delta} />}
      {sub && <span className="text-[11px] text-stone-500">{sub}</span>}
    </div>
  </Card>
);

export function BiKpis({ data }) {
  const fm = useFm();
  const c = data.kpi.current, dl = data.kpi.delta_pct, n = data.notes;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="bi-kpis">
      <Kpi label="Fatturato DDT (imponibile)" value={fm(c.revenue)} extra={`IVA inclusa ${fm(c.revenue_gross)}`} delta={dl.revenue} sub={`doc. ${fm(c.revenue_doc)}`} icon={Euro} note={`${n.ddt} ${n.vat} Il valore "doc." è la somma degli imponibili di testata (include spese).`} testId="bi-kpi-revenue" />
      <Kpi label="Kg venduti" value={`${fmtKg(c.kg, 0)} kg`} delta={dl.kg} sub={c.gift_kg > 0 ? `di cui omaggio ${fmtKg(c.gift_kg, 1)}` : `${fmtInt(c.pieces)} colli`} icon={Scale} accent="text-stone-900" note={n.ddt} testId="bi-kpi-kg" />
      <Kpi label="N. DDT" value={fmtInt(c.ddt_count)} delta={dl.ddt_count} sub={c.avg_ddt_value != null ? `medio ${fm(c.avg_ddt_value)}` : null} icon={FileText} accent="text-stone-900" note={`${n.ddt} Fonti nel periodo: ${data.sources.etifood_docs} import Etifood + ${data.sources.etiflor_docs} sync Etiflor, ${data.sources.duplicates_removed} doppioni esclusi.`} testId="bi-kpi-ddt" />
      <Kpi label="Clienti serviti" value={fmtInt(c.customers)} delta={dl.customers} sub={`${fmtInt(c.products)} articoli`} icon={Users} accent="text-stone-900" note={n.customers} testId="bi-kpi-customers" />
      <Kpi label="Prezzo medio" value={c.avg_price_kg != null ? `${fm(c.avg_price_kg)}/kg` : "—"} delta={dl.avg_price_kg} sub="sui kg a pagamento" icon={Tag} accent="text-emerald-700" note={`${n.ddt} Prezzo medio = imponibile righe ÷ kg non omaggio.`} testId="bi-kpi-avg-price" />
    </div>
  );
}

const Count = ({ label, value, prev, tone = "text-stone-900", testId }) => (
  <div className="flex-1 min-w-[120px]" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${tone}`}>{fmtInt(value)}</p>
    <p className="text-[11px] text-stone-400">prec. {fmtInt(prev)}</p>
  </div>
);

export function BiOrders({ data }) {
  const o = data.orders.current, p = data.orders.previous;
  return (
    <Card className="bg-white border-stone-200 p-4" data-testid="bi-orders">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#8A1C2A]" /> Ordini app <span className="text-xs font-normal text-stone-500">solo conteggi</span></h3>
        <SourceNote text={data.notes.orders} testId="bi-orders-source" />
      </div>
      <div className="flex flex-wrap gap-4">
        <Count label="Ricevuti" value={o.received} prev={p.received} testId="bi-orders-received" />
        <Count label="Eseguiti" value={o.executed} prev={p.executed} tone="text-emerald-700" testId="bi-orders-executed" />
        <Count label="In lavorazione" value={o.in_progress} prev={p.in_progress} tone="text-amber-700" testId="bi-orders-progress" />
        <Count label="Annullati" value={o.cancelled} prev={p.cancelled} tone="text-stone-500" testId="bi-orders-cancelled" />
      </div>
    </Card>
  );
}

export function BiFinance({ data, onOpenStatements }) {
  const fm = useFm();
  const f = data.finance;
  if (!f || !f.docs_count) return null;
  const items = [
    ["Fatturato", f.invoiced, "text-[#8A1C2A]", "bi-fin-invoiced"],
    ["Incassato", f.collected, "text-emerald-700", "bi-fin-collected"],
    ["Aperto", f.open, "text-blue-700", "bi-fin-open"],
    ["Scaduto", f.overdue, "text-red-700", "bi-fin-overdue"],
  ];
  return (
    <Card className="bg-white border-stone-200 p-4" data-testid="bi-finance">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2"><Banknote className="w-4 h-4 text-[#8A1C2A]" /> Finanza <span className="text-xs font-normal text-stone-500">{fmtInt(f.docs_count)} documenti fiscali · tasso incasso {f.collection_rate}%{f.excluded_docs > 0 ? ` · ${fmtInt(f.excluded_docs)} esclusi` : ""}</span></h3>
        <div className="flex items-center gap-1">
          <SourceNote text={`${data.notes.finance} ${data.notes.exclusions}`} testId="bi-finance-source" />
          <button type="button" onClick={onOpenStatements} className="h-10 px-3 rounded-xl text-sm font-semibold text-[#8A1C2A] hover:bg-[#8A1C2A]/5 inline-flex items-center gap-1" data-testid="bi-open-statements">Apri Scadenziario <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map(([l, v, tone, tid]) => (
          <div key={l} data-testid={tid}>
            <p className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold">{l}</p>
            <p className={`text-lg sm:text-xl font-bold tabular-nums ${tone}`}>{fm(v)}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}
